/**
 * Client-side helper for the parental-consent commit (Step 7).
 *
 * Calls the `commit-parental-consent` edge function. Body shape is
 * the validated set from the consent draft (DOB + parent email + the
 * 6-categoy ticks + verification flag).
 *
 * Pattern matches the other `@4cm/core/auth` helpers (fileDsrRequest,
 * setAiOptIn, etc.). Discriminated result.
 */

import type { UkConsentCategory } from '@4cm/core/auth';

export interface CommitConsentArgs {
  supabaseUrl: string;
  /** Public anon key — function is called pre-auth (parent has no
   *  account yet); anon key for the apikey header. */
  anonKey: string;
  parentEmail: string;
  /** ISO YYYY-MM-DD. */
  childDob: string;
  consents: UkConsentCategory[];
  inviteToken?: string;
  /** True after Yoti or dev-bypass has marked the parent as verified. */
  verified: boolean;
  /** Optional. Defaults to 'v1.0' server-side. */
  noticeVersion?: string;
  /** Optional fetch override for tests. */
  fetchImpl?: typeof fetch;
}

export interface CommitConsentResult {
  ok: boolean;
  parentUserId?: string;
  consentCount?: number;
  consentsWritten?: UkConsentCategory[];
  nextStep?: string;
  error?: string;
  status?: number;
}

export async function commitParentalConsent(args: CommitConsentArgs): Promise<CommitConsentResult> {
  const { supabaseUrl, anonKey, parentEmail, childDob, consents, inviteToken, verified, noticeVersion } = args;
  const fetchImpl = args.fetchImpl ?? fetch;

  if (!supabaseUrl || !anonKey) {
    return { ok: false, error: 'supabase env not configured' };
  }
  const url = `${supabaseUrl.replace(/\/$/, '')}/functions/v1/commit-parental-consent`;
  const resp = await fetchImpl(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${anonKey}`,
      apikey: anonKey,
    },
    body: JSON.stringify({
      parent_email: parentEmail,
      child_dob: childDob,
      consents,
      invite_token: inviteToken,
      verified,
      notice_version: noticeVersion,
    }),
  });

  let parsed: Record<string, unknown> | null = null;
  try { parsed = (await resp.json()) as Record<string, unknown>; } catch { /* ignore */ }
  if (!resp.ok) {
    return {
      ok: false,
      status: resp.status,
      error:
        (parsed && typeof parsed.error === 'string' && parsed.error) ||
        `commit failed (${resp.status})`,
    };
  }
  return {
    ok: parsed?.ok === true,
    parentUserId: typeof parsed?.parent_user_id === 'string' ? parsed.parent_user_id : undefined,
    consentCount: typeof parsed?.consent_count === 'number' ? parsed.consent_count : undefined,
    consentsWritten: Array.isArray(parsed?.consents_written)
      ? (parsed.consents_written as UkConsentCategory[])
      : undefined,
    nextStep: typeof parsed?.next_step === 'string' ? parsed.next_step : undefined,
  };
}
